import { MediaService } from 'openapi'
import { useCallback } from 'react'
import { ulid } from 'ulid'

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024 // 10MB

export type UploadErrorReason = 'too_large' | 'upload_failed' | 'aborted'

export class UploadError extends Error {
  reason: UploadErrorReason
  constructor(reason: UploadErrorReason, message?: string) {
    super(message ?? reason)
    this.name = 'UploadError'
    this.reason = reason
  }
}

export interface UploadFileResult {
  attachmentId: string
  publicUrl: string
}

export interface UploadFileOptions {
  maxBytes?: number
  signal?: AbortSignal
}

export function useUploadFile() {
  return useCallback(
    async (file: File, options: UploadFileOptions = {}): Promise<UploadFileResult> => {
      const { maxBytes = DEFAULT_MAX_BYTES, signal } = options

      if (file.size > maxBytes) {
        throw new UploadError('too_large', `File exceeds ${maxBytes / 1024 / 1024}MB limit`)
      }

      if (signal?.aborted) {
        throw new UploadError('aborted', 'Upload aborted')
      }

      const attachmentId = ulid()
      const promise = MediaService.mediaControllerUploadFileV1({ file, id: attachmentId })

      const onAbort = () => promise.cancel()
      signal?.addEventListener('abort', onAbort)

      try {
        const attachment = await promise
        return { attachmentId, publicUrl: attachment.publicUrl }
      } catch (err) {
        if (signal?.aborted) {
          throw new UploadError('aborted', 'Upload aborted')
        }
        const msg = err instanceof Error ? err.message : 'Upload failed'
        throw new UploadError('upload_failed', msg)
      } finally {
        signal?.removeEventListener('abort', onAbort)
      }
    },
    []
  )
}
